/**
 * VitaCoinsBonus — o fim da cadeia de recompensa: "Você ganhou N VitaCoins",
 * com a oferta de vídeo bônus. Fonte: handoffAnuncios/PROMPT-claude-code.md
 * § 3 e as telas 3A–3E de html/Monetizacao.dc.html.
 *
 * Estados:
 *   A  ganho, com CTA amarelo "Assista um vídeo e leve X" + link "Fico com os N"
 *   B  vídeo carregando (spinner no CTA, link desabilitado)
 *   C  vídeo concluído: hexágono 140 + faíscas, conta N → N + bônus
 *   D  sem vídeo (podeVideo=false ou SEM_ANUNCIO): só "Continuar"
 *   E  vídeo não concluído / erro: toast 2,5s e volta para A
 *   F  assistiu tudo e o SSV não chegou (AGUARDANDO): toast honesto e D
 *
 * NENHUMA regra de negócio aqui. `valor`, `saldo` e o bônus vêm de quem
 * chama; o `multExibido` só monta a frase do CTA. O total do estado C é
 * `valor + r.valorBonus` — o servidor manda o bônus real, e o cliente não
 * recalcula multiplicador nenhum.
 *
 * `onAssistir()` → Promise<{ status, valorBonus?, saldoApos? }> com status
 * 'CONCLUIDO' | 'SEM_ANUNCIO' | 'NAO_FINALIZADO' | 'AGUARDANDO' | 'ERRO'.
 * Uma rejeição conta como 'ERRO'.
 *
 * Fecha por "Fico com os N", "Continuar", Esc e voltar do Android — nunca
 * enquanto o vídeo carrega (B), para não abrir a próxima tela por baixo do
 * anúncio.
 */

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../design/icons/Icon';
import useSoundEffect from '../../hooks/useSoundEffect';
import useOverlayScrollLock from '../../hooks/useOverlayScrollLock';
import haptics from '../../design/feedback/haptics';
import cx from '../../design/cx';
import { formatarNumero } from '../../i18n/formatos';
import { registrarOffcanvas, removerOffcanvas } from '../../utils/offcanvasStack';
import coinSound from '../../Assets/sounds/coin.mp3';
import s from './VitaCoinsBonus.module.scss';

/** Quando o número entra (ms) — casa com o atraso do `.textos` no módulo. */
const ATRASO_NUMERO = 600;
/** Contagem 0 → N na entrada. */
const DURACAO_GANHO = 800;
/** Contagem N → N + bônus no estado C. */
const DURACAO_BONUS = 1200;
/** Toast do vídeo não concluído: visível + saída. */
const TOAST_VISIVEL = 2500;
const TOAST_SAIDA = 240;

const reduzido = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const inteiro = (v) => Math.max(0, Math.round(Number(v) || 0));

export default function VitaCoinsBonus({
  valor,
  saldo,
  multExibido = 2.5,
  podeVideo,
  onAssistir,
  onClose,
}) {
  const { t } = useTranslation('moedas');
  const { play } = useSoundEffect();
  const idTitulo = useId();

  const base = inteiro(valor);
  const comVideo = !!podeVideo && typeof onAssistir === 'function';
  const oferta = inteiro(base * (Number(multExibido) || 1));

  const [fase, setFase] = useState(comVideo ? 'A' : 'D');
  const [exibido, setExibido] = useState(() => (reduzido() ? base : 0));
  const [total, setTotal] = useState(base);
  const [saldoAtual, setSaldoAtual] = useState(saldo ?? null);
  const [flash, setFlash] = useState(() => !reduzido());
  const [toast, setToast] = useState(null); // null | 'entra' | 'sai'
  // Qual frase o toast está mostrando: 'nao_finalizado' (fechou o vídeo antes
  // do fim) ou 'aguardando' (assistiu tudo e a confirmação do servidor não
  // chegou a tempo). Sair a mesma frase nos dois casos era acusar de não ter
  // assistido quem assistiu.
  const [toastChave, setToastChave] = useState('nao_finalizado');

  const faseRef = useRef(fase);
  faseRef.current = fase;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onAssistirRef = useRef(onAssistir);
  onAssistirRef.current = onAssistir;
  const baseInicial = useRef(base);
  const saldoDoBonus = useRef(false);
  const montado = useRef(true);
  const fechado = useRef(false);
  const raf = useRef(0);
  const timersToast = useRef([]);

  useOverlayScrollLock(true);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
      cancelAnimationFrame(raf.current);
      timersToast.current.forEach(clearTimeout);
    };
  }, []);

  // Saldo que chega depois (carregado em paralelo) entra na linha — a não ser
  // que o servidor já tenha mandado o saldo pós-bônus.
  useEffect(() => {
    if (!saldoDoBonus.current) setSaldoAtual(saldo ?? null);
  }, [saldo]);

  const contar = useCallback((de, ate, duracao) => {
    cancelAnimationFrame(raf.current);
    if (reduzido() || de === ate) {
      setExibido(ate);
      return;
    }
    let inicio = null;
    const passo = (ts) => {
      inicio ??= ts;
      const p = Math.min(1, (ts - inicio) / duracao);
      const suave = 1 - Math.pow(1 - p, 3);
      setExibido(Math.round(de + (ate - de) * suave));
      if (p < 1) raf.current = requestAnimationFrame(passo);
    };
    raf.current = requestAnimationFrame(passo);
  }, []);

  // Entrada: flash + moeda + haptic, e a contagem 0 → N quando o número sobe.
  useEffect(() => {
    play(coinSound, { volume: 0.8 });
    haptics.success();
    const timer = setTimeout(() => contar(0, baseInicial.current, DURACAO_GANHO), ATRASO_NUMERO);
    return () => clearTimeout(timer);
  }, [contar, play]);

  const fechar = useCallback(() => {
    if (fechado.current || faseRef.current === 'B') return;
    fechado.current = true;
    haptics.light();
    onCloseRef.current?.();
  }, []);

  // Voltar do Android e Esc — ignorados em B (o link também está desligado).
  useEffect(() => {
    const id = registrarOffcanvas(fechar);
    const aoTeclar = (e) => {
      if (e.key === 'Escape') fechar();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => {
      window.removeEventListener('keydown', aoTeclar);
      removerOffcanvas(id);
    };
  }, [fechar]);

  /**
   * Bônus confirmado: conta o total ATUAL até o total com bônus (15 → 38) e
   * atualiza o saldo. Um só lugar, usado tanto pela confirmação rápida quanto
   * pela tardia — as duas têm que dar exatamente a mesma tela.
   */
  const concluir = (r) => {
    const novoTotal = base + inteiro(r.valorBonus);
    if (novoTotal <= base) {
      // Confirmou sem bônus nenhum: não há animação para rodar.
      setFase('D');
      return;
    }
    if (r.saldoApos != null) {
      saldoDoBonus.current = true;
      setSaldoAtual(r.saldoApos);
    }
    setTotal(novoTotal);
    setFase('C');
    play(coinSound, { volume: 0.8 });
    haptics.success();
    contar(base, novoTotal, DURACAO_BONUS);
  };

  const mostrarToast = (chave = 'nao_finalizado') => {
    timersToast.current.forEach(clearTimeout);
    setToastChave(chave);
    setToast('entra');
    timersToast.current = [
      setTimeout(() => montado.current && setToast('sai'), TOAST_VISIVEL),
      setTimeout(() => montado.current && setToast(null), TOAST_VISIVEL + TOAST_SAIDA),
    ];
  };

  const assistir = async () => {
    if (faseRef.current !== 'A') return;
    haptics.light();
    faseRef.current = 'B';
    setFase('B');

    let r;
    try {
      r = await onAssistirRef.current?.();
    } catch {
      r = { status: 'ERRO' };
    }
    if (!montado.current) return;

    const status = r?.status;
    if (status === 'CONCLUIDO') {
      concluir(r);
      return;
    }
    cancelAnimationFrame(raf.current);
    setExibido(base);
    if (status === 'SEM_ANUNCIO') {
      setFase('D');
      return;
    }
    // AGUARDANDO: o vídeo FOI até o fim e a confirmação do servidor ainda não
    // chegou. Não volta para A — oferecer o vídeo de novo faria a pessoa
    // assistir um segundo anúncio por um bônus que o servidor já amarrou a
    // este mesmo ato (MOEDA_BONUS_ANUNCIO) e vai recusar. Fica em D, liberada
    // para seguir, e a confirmação continua correndo por baixo: quando chega,
    // `concluir` roda a MESMA animação do caminho rápido (o total subindo até
    // o valor com bônus). Quem não esperar não perde o crédito, só a animação.
    if (status === 'AGUARDANDO') {
      setFase('D');
      mostrarToast('aguardando');
      Promise.resolve(r.confirmacao)
        .then((tardio) => {
          if (!montado.current || fechado.current) return;
          if (tardio?.status === 'CONCLUIDO') concluir(tardio);
        })
        .catch(() => {});
      return;
    }
    // NAO_FINALIZADO, ERRO ou qualquer coisa inesperada: E → volta para A.
    haptics.warning();
    setFase('A');
    mostrarToast();
  };

  const bonus = fase === 'C';
  const carregando = fase === 'B';
  const comOferta = fase === 'A' || fase === 'B';

  const conteudo = (
    <div
      className={cx(s.raiz, bonus && s.raizBonus)}
      // Tela desenhada no escuro: no tema claro a pílula clara (text.1 com
      // texto onPrimary) viraria preto sobre preto. Os tokens mudam por
      // [data-theme].
      data-theme="dark"
      role="dialog"
      aria-modal="true"
      aria-labelledby={idTitulo}
    >
      {flash && (
        <div
          className={s.flash}
          aria-hidden="true"
          onAnimationEnd={() => setFlash(false)}
        />
      )}

      {toast && (
        <div className={cx(s.toast, toast === 'sai' && s.toastSai)} role="status">
          <Icon name="action.info" size={18} />
          <span>{t(`bonus.${toastChave}`)}</span>
        </div>
      )}

      <div className={s.palco}>
        {/* A chave remonta a moeda no C para o pop rodar de novo. */}
        <div key={bonus ? 'moeda-bonus' : 'moeda'} className={cx(s.moeda, bonus && s.moedaBonus)} aria-hidden="true">
          <div className={s.brilho} />
          {/* O símbolo oficial (IconeCoin), e não um hexágono de CSS com um
              "V" de texto por cima: uma moeda só no app inteiro. */}
          <Icon name="coin" size={bonus ? 140 : 120} className={s.simbolo} />
          {bonus && (
            <>
              <span className={s.faisca}>✦</span>
              <span className={s.faisca}>✦</span>
              <span className={s.faisca}>✦</span>
            </>
          )}
        </div>

        <div className={s.textos} aria-live="polite">
          <span className={cx(s.numero, bonus && s.numeroBonus)} aria-hidden="true">
            {formatarNumero(exibido, { sinal: true })}
          </span>
          <h2 id={idTitulo} className={cx(s.titulo, bonus && s.tituloBonus)}>
            {bonus
              ? t('bonus.agora_sim', { count: total, valor: formatarNumero(total) })
              : t('bonus.ganhou', { count: base, valor: formatarNumero(base) })}
          </h2>
          {saldoAtual != null && (
            <p className={s.saldo}>
              {t('bonus.saldo', { valor: formatarNumero(saldoAtual) })}
            </p>
          )}
        </div>
      </div>

      <div className={s.rodape}>
        {comOferta ? (
          <>
            <button
              type="button"
              className={cx(s.cta, s.ctaPrimario, carregando && s.carregando)}
              onClick={assistir}
              disabled={carregando}
              aria-busy={carregando || undefined}
              autoFocus
            >
              {carregando ? (
                <>
                  <span className={s.spinner} aria-hidden="true" />
                  {t('bonus.carregando')}
                </>
              ) : (
                <>
                  <Icon name="action.play" size={18} />
                  {t('bonus.assistir', { count: oferta, valor: formatarNumero(oferta) })}
                </>
              )}
            </button>
            <button type="button" className={s.link} onClick={fechar} disabled={carregando}>
              {t('bonus.ficar', { count: base, valor: formatarNumero(base) })}
            </button>
          </>
        ) : (
          <button type="button" className={cx(s.cta, s.ctaClaro)} onClick={fechar} autoFocus>
            {t('bonus.continuar')}
          </button>
        )}
      </div>
    </div>
  );

  return typeof document === 'undefined' ? conteudo : createPortal(conteudo, document.body);
}
